import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	OnDestroy,
	OnInit,
	Output,
} from "@angular/core";
import { NgModel } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";

@Component({
	selector: "app-time-duration-selector",
	templateUrl: "./time-duration-selector.component.html",
	styleUrls: ["./time-duration-selector.component.scss"],
})
export class TimeDurationSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
	isOpen = false;
	durationInMinutes = 0;
	timeInput = "";
	showLabel = true;
	filterButtonTextClass = "";
	optionSelected = "";
	oldTimeInput = "";
	stateKey = "inspection-history";
	destroyed$ = new Subject<void>();
	@Output() submitTimeDuration = new EventEmitter<number>();

	constructor(private elementRef: ElementRef, private memCacheService: MemCacheService) {}

	ngAfterViewInit(): void {
		const state = this.memCacheService.getValue<any>(this.stateKey)?.durationInMinutes;
		if (state) {
			this.durationInMinutes = state;
			this.updateDisplayInput();
			this.filterButtonTextClass = "filter-button-text-value-selected";
			this.showLabel = false;
			this.oldTimeInput = this.timeInput;
			this.optionSelected = this.formatDurationToHHMM(this.durationInMinutes);
		} else {
			this.resetDefaultValues();
		}
	}

	ngOnInit(): void {
		this.memCacheService
			.cleared$()
			.pipe(takeUntil(this.destroyed$))
			.subscribe(() => {
				this.memCacheService.setValue(this.stateKey, null);
				this.resetDefaultValues();
			});
	}

	resetDefaultValues(): void {
		this.durationInMinutes = 0;
		this.showLabel = true;
		this.isOpen = false;
		this.filterButtonTextClass = "";
		this.optionSelected = "";
		this.timeInput = "";
		this.oldTimeInput = "";
	}

	toggleDropdown(): void {
		this.isOpen = !this.isOpen;
		this.timeInput = this.oldTimeInput;
		this.calculateDurationInMinutes();
	}

	pad(num: number): string {
		return num.toString().padStart(2, "0");
	}

	updateDisplayInput(): void {
		const h = Math.floor(this.durationInMinutes / 60);
		const m = this.durationInMinutes % 60;
		this.timeInput = `${this.pad(h)}:${this.pad(m)}`;
	}

	onSliderChange(value: number): void {
		this.durationInMinutes = value * 15;
		this.updateDisplayInput();
	}

	onTimeInputChange(control: NgModel): void {
		// reset filter
		if (this.timeInput === "") {
			control.control.setErrors(null);
			this.durationInMinutes = null;
			return;
		}

		const match =
			this.timeInput.match(/^(0?[0-9]|1[0-5]):([0-5][0-9])$/) || this.timeInput.match(/^(0?[0-9]|1[0-5])$/);

		if (!match) {
			control.control.setErrors({ validWithDuration: true });
			this.durationInMinutes = null;
			return;
		}

		const hours = parseInt(match[1], 10);
		const minutes = match[2] ? parseInt(match[2], 10) : 0;

		if (hours > 15 || (hours === 15 && minutes > 0)) {
			control.control.setErrors({ validWithDuration: true });
			this.durationInMinutes = null;
			return;
		}

		control.control.setErrors(null);
		this.durationInMinutes = hours * 60 + minutes;
		control.control.markAsTouched();
	}

	calculateDurationInMinutes(): void {
		if (!this.timeInput) {
			this.durationInMinutes = 0;
			return;
		}
		const hours = parseInt(this.timeInput.split(":")[0], 10);
		const minutes = parseInt(this.timeInput.split(":")[1], 10);
		this.durationInMinutes = hours * 60 + minutes;
	}

	blockNonTimeCharacters(event: KeyboardEvent): void {
		const allowed = /^[0-9:]$/;
		if (!allowed.test(event.key)) {
			event.preventDefault();
		}
	}

	onKeyup(event: KeyboardEvent, timeInputRef): void {
		const key = event.key;
		const input = this.timeInput?.trim() ?? "";

		if (key !== "Backspace" && key !== "Delete") {
			this.onTimeInputChange(timeInputRef);
			return;
		}

		// Allow backspace/delete with valid hour-only input
		const hourOnlyMatch = input.match(/^(0?[0-9]|1[0-5])$/);
		if (hourOnlyMatch) {
			const hours = parseInt(hourOnlyMatch[1], 10);
			this.durationInMinutes = hours * 60;
			return;
		}

		// Fallback to full validation
		this.onTimeInputChange(timeInputRef);
	}

	onBlur(): void {
		// Validate time input format HH:MM
		const onlyHour = /^0?[0-9]$|^1[0-5]$/.exec(this.timeInput);
		if (onlyHour) {
			this.timeInput = onlyHour[0].padStart(2, "0") + ":00";
		}
	}

	// Reset duration filter state
	private resetDurationFilterState(): void {
		this.filterButtonTextClass = "filter-button-text-value-not-selected";
		this.showLabel = true;
		this.oldTimeInput = "";
		this.optionSelected = "";
	}

	// Handle apply button click
	onApply(): void {
		this.isOpen = false;

		// Case when duration is empty and user don't input anything, don't emit anything, just clear the filter
		if (!this.durationInMinutes && !this.oldTimeInput) {
			this.resetDurationFilterState();
			return;
		} else if (!this.durationInMinutes) {
			// Case when duration has data, user remove value in input/slider => reset filter and call get all inspections
			this.resetDurationFilterState();
			this.submitTimeDuration.emit(this.durationInMinutes);
			return;
		}

		// Case when user typed valid time in the input/slider
		this.filterButtonTextClass = "filter-button-text-value-selected";
		this.showLabel = false;
		this.oldTimeInput = this.timeInput;
		this.optionSelected = this.formatDurationToHHMM(this.durationInMinutes);
		this.submitTimeDuration.emit(this.durationInMinutes);
		this.memCacheService.setValue(this.stateKey, {
			durationInMinutes: this.durationInMinutes,
		});
	}

	// Format duration in HH:MM
	// If minutes is 0, return "00:00"
	formatDurationToHHMM(minutes: number): string {
		const hrs = Math.floor(minutes / 60);
		const mins = minutes % 60;
		const paddedHrs = hrs.toString().padStart(2, "0");
		const paddedMins = mins.toString().padStart(2, "0");
		return `${paddedHrs}:${paddedMins}`;
	}

	onCancel(): void {
		this.durationInMinutes = 0;
		this.timeInput = this.oldTimeInput;
		this.isOpen = false;
		this.durationInMinutes = 0;
	}

	@HostListener("document:click", ["$event"])
	clickOutside(event: MouseEvent): void {
		if (!this.elementRef.nativeElement.contains(event.target)) {
			this.isOpen = false;
		}
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
